/**
 * "Who edited this" markers, for record history.
 *
 * History is recorded by the record-history-capture function, which sweeps
 * every few minutes for rows that changed and have gone quiet. A sweep runs
 * with an API key, so it cannot see who made a change. The admin app tells it:
 * when it writes a captured record, it also writes a tiny marker row
 * (`history_touches`, in the flickquote-system database: table and row id) granted `read("user:<me>")`.
 * Appwrite refuses to let a client grant a role it does not hold, so that grant
 * is proof of who wrote the marker — the sweep trusts it, not a column.
 *
 * DEBOUNCED BY DESIGN. Auto-save writes a record every pause in typing; a
 * marker per write would be a second write per keystroke pause for nothing.
 * One marker per record per user per THROTTLE_MS is enough: the sweep waits for
 * two quiet minutes before recording a change, so a burst of edits is one entry
 * and one marker covers it. A marker is a plain row write — it never starts a
 * function.
 *
 * Every write in the app goes through the `databases` instance exported by
 * services/appwrite.js, which is wrapped with `withHistoryMarkers`, so no call
 * site has to remember any of this.
 */

/** The product database. */
export const DATABASE_ID = 'flickquote';

/** Where history lives: the system database, not the product one. */
export const HISTORY_DATABASE_ID = 'flickquote-system';

/** Where markers go. Must match history_touches in appwrite.config.json. */
export const MARKERS_TABLE = 'history_touches';

/** At most one marker per record per user this often. */
export const THROTTLE_MS = 60 * 1000;

/**
 * The tables history captures — exactly those with the `history_sweep_idx`
 * index in appwrite.config.json. Pinned by lib/historyMarkers.test.js, as the
 * functions' registry is by its own test.
 */
export const CAPTURED_TABLES = [
  'quote_requests',
  'quote_request_images',
  'notes',
  'quotes',
  'vehicle_damage',
  'customers',
  'customer_vehicles',
  'customer_organisations',
  'customer_organisation_links',
  'customer_relationships',
  'fleet_vehicles',
  'fleet_bookings',
  'fleet_vehicle_events',
  'org_line_allowances',
  'panel-quote-insurer-map',
];

const captured = new Set(CAPTURED_TABLES);

/**
 * The marker writer.
 *
 *   write(tableName, rowId, userId)  writes one marker; may reject
 *   currentUser()                    resolves to the signed-in user, or null
 *   now()                            the clock, injectable for tests
 *
 * Returns `note(databaseId, tableName, rowId)`, which never throws and never
 * waits on the marker: a history marker must not slow or break a save.
 */
export function createHistoryMarkers({ write, currentUser, now = () => Date.now() }) {
  const lastWritten = new Map(); // `${userId}:${table}:${rowId}` -> ms

  return function note(databaseId, tableName, rowId) {
    if (databaseId !== DATABASE_ID || !captured.has(tableName) || !rowId) return;

    Promise.resolve()
      .then(currentUser)
      .then((user) => {
        const userId = user?.id || user?.$id;
        if (!userId) return undefined;
        const key = `${userId}:${tableName}:${rowId}`;
        const at = now();
        if (at - (lastWritten.get(key) ?? -Infinity) < THROTTLE_MS) return undefined;
        lastWritten.set(key, at);
        return Promise.resolve(write(tableName, rowId, userId)).catch((err) => {
          // Not remembered, so the next save tries again.
          lastWritten.delete(key);
          // eslint-disable-next-line no-console
          console.debug('History marker not written:', err?.message);
        });
      })
      .catch(() => {});
  };
}

/** The arguments of a databases call, positional or as one params object. */
function callArgs(args, names) {
  if (args.length === 1 && args[0] && typeof args[0] === 'object' && 'collectionId' in args[0]) {
    return args[0];
  }
  return Object.fromEntries(names.map((name, i) => [name, args[i]]));
}

/**
 * Wrap a Databases instance so creates and updates of captured records note
 * who made them. The instance's other methods, and the results of these two,
 * are untouched.
 */
export function withHistoryMarkers(databases, note) {
  const create = databases.createDocument.bind(databases);
  const update = databases.updateDocument.bind(databases);

  databases.createDocument = async (...args) => {
    const result = await create(...args);
    const { databaseId, collectionId } = callArgs(args, ['databaseId', 'collectionId']);
    note(databaseId, collectionId, result?.$id);
    return result;
  };

  databases.updateDocument = async (...args) => {
    const result = await update(...args);
    const { databaseId, collectionId, documentId } = callArgs(args, ['databaseId', 'collectionId', 'documentId']);
    note(databaseId, collectionId, documentId || result?.$id);
    return result;
  };

  return databases;
}
